import SortOutlinedIcon from '@mui/icons-material/SortOutlined';
import { r, s } from '@/theme/tokens';
import { Stack } from '@/ui';
import { SingleSelectFilterField } from '@/ui/composites/filters/SingleSelectFilterField';
import { Popover, Text } from '@/ui/primitives';
import type { ReactElement } from 'react';

export type TableSortOption<TSortKey extends string> = { key: TSortKey; label: string };
const popoverFontSize = 13;

type TableSortPopoverProps<TSortKey extends string> = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  sortKey: TSortKey;
  sortDirection: 'asc' | 'desc';
  sortOptions: Array<TableSortOption<TSortKey>>;
  onSortChange: (sortKey: TSortKey, sortDirection: 'asc' | 'desc') => void;
  preserveWindowScroll: (action: () => void) => void;
};

export const TableSortPopover = <TSortKey extends string>({
  open,
  anchorEl,
  onClose,
  sortKey,
  sortDirection,
  sortOptions,
  onSortChange,
  preserveWindowScroll,
}: TableSortPopoverProps<TSortKey>): ReactElement => (
  <Popover
    open={open}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    paperSx={{
      backgroundColor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: `${r('md')} !important`,
      overflow: 'hidden !important',
    }}
  >
    <Stack gap="none">
      <Stack
        gap="none"
        sx={{ px: s('lg'), py: s('md'), borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Stack
          direction="row"
          alignItems="center"
          gap="sm"
          sx={{ '& .MuiSvgIcon-root': { color: 'text.secondary' } }}
        >
          <SortOutlinedIcon sx={{ width: '16px !important', height: '16px !important' }} />
          <Text
            role="label"
            color="textSecondary"
            sx={{
              fontSize: `${popoverFontSize}px`,
              lineHeight: 1.4,
            }}
          >
            Sort
          </Text>
        </Stack>
      </Stack>
      <Stack gap="md" sx={{ pl: s('xl'), pr: s('lg'), py: s('lg') }}>
        <SingleSelectFilterField
          label="Sort By"
          fontSizePx={popoverFontSize}
          labelWidthPx={72}
          labelControlGap={s('md')}
          inputAriaLabel="Sort by field"
          value={sortKey}
          options={sortOptions.map((option) => ({ value: option.key, label: option.label }))}
          onChange={(nextSortKey) => {
            preserveWindowScroll(() => {
              onSortChange(nextSortKey as TSortKey, sortDirection);
            });
          }}
          controlWidthPx={180}
          inputBackgroundColor="background.default"
          inputLeftPadding={s('xs')}
        />
        <SingleSelectFilterField
          label="Direction"
          fontSizePx={popoverFontSize}
          labelWidthPx={72}
          labelControlGap={s('md')}
          inputAriaLabel="Sort direction"
          value={sortDirection}
          options={[
            { value: 'asc', label: 'Ascending' },
            { value: 'desc', label: 'Descending' },
          ]}
          onChange={(nextSortDirection) => {
            preserveWindowScroll(() => {
              onSortChange(sortKey, nextSortDirection as 'asc' | 'desc');
            });
          }}
          controlWidthPx={180}
          inputBackgroundColor="background.default"
          inputLeftPadding={s('xs')}
        />
      </Stack>
    </Stack>
  </Popover>
);
